/**
 * Route helpers for standalone dev vs CRM-hosted module federation.
 * Hosted shell mounts this remote under `/document-builder/*`.
 */
export const DOCUMENT_BUILDER_HOST_PREFIX = '/document-builder';

/** Coerce router pathname (may be undefined in tests) to a safe string. */
export function coercePathname(pathname?: string | null): string {
  return typeof pathname === 'string' ? pathname : '';
}

/** Normalize slashes — never calls `.replace` on undefined. */
export function normalizeDocumentBuilderPath(path: string): string {
  const safe = coercePathname(path);
  if (!safe) return '/';
  const collapsed = safe.replace(/\/{2,}/g, '/');
  if (collapsed.length > 1 && collapsed.endsWith('/')) {
    return collapsed.replace(/\/+$/, '');
  }
  return collapsed;
}

function coercePathSegment(segment: string | null | undefined): string {
  if (typeof segment === 'string') return segment;
  if (segment == null) return '';
  return String(segment);
}

/** Join a base path and segments without duplicate slashes. */
export function joinDocumentBuilderPath(basePath: string | null | undefined, ...segments: string[]): string {
  const base = normalizeDocumentBuilderPath(coercePathname(basePath) || '');
  const tail = segments
    .map((segment) => coercePathSegment(segment).replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');

  if (!base || base === '/') {
    return tail ? `/${tail}` : '/';
  }

  return tail ? `${base}/${tail}` : base;
}

export function isDocumentBuilderHosted(pathname?: string | null): boolean {
  const safe = coercePathname(pathname);
  return safe === DOCUMENT_BUILDER_HOST_PREFIX || safe.startsWith(`${DOCUMENT_BUILDER_HOST_PREFIX}/`);
}

/**
 * Resolves `/document-builder` when hosted, or `''` for standalone.
 * Explicit `basePath` wins when provided.
 */
export function resolveDocumentBuilderBasePath(pathname?: string | null, basePath?: string | null): string {
  if (typeof basePath === 'string') {
    return basePath === '/' ? '' : basePath;
  }

  return isDocumentBuilderHosted(pathname) ? DOCUMENT_BUILDER_HOST_PREFIX : '';
}

export function documentBuilderBlocksPath(pathname?: string | null, basePath?: string | null): string {
  return joinDocumentBuilderPath(resolveDocumentBuilderBasePath(pathname, basePath), 'blocks');
}

export function documentBuilderTemplatesPath(pathname?: string | null, basePath?: string | null): string {
  return joinDocumentBuilderPath(resolveDocumentBuilderBasePath(pathname, basePath), 'templates');
}

export function documentBuilderBlockPath(blockId: string, pathname?: string | null, basePath?: string | null): string {
  return joinDocumentBuilderPath(resolveDocumentBuilderBasePath(pathname, basePath), 'blocks', blockId);
}

/** Absolute path to a template editor screen for the current hosting mode. */
export function documentBuilderTemplatePath(templateId: string, pathname?: string | null, basePath?: string | null): string {
  return joinDocumentBuilderPath(resolveDocumentBuilderBasePath(pathname, basePath), 'templates', templateId);
}
